import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {makeSelectLeagueMatchDaysById, selectSportsType} from "../selectors";
import {LanguageLocaleEnum} from "../client/api";
import {AppDispatch} from "../store";
import {translate} from "../config/translator";
import {fetchMatchDaysOfLeague, fetchMatchesByLeagueAndMatchDay} from "../store/actions/matchActions";

type Params = {
    leagueId?: number;
    isCup: boolean;
    sports: ReturnType<typeof selectSportsType>;
    leagueMatchDays?: number | null;
    currentMatchDay?: number | null;
    l: LanguageLocaleEnum;
    hasTotwRestriction: boolean;
};

export function useLeagueMatchday({
                                      leagueId,
                                      isCup,
                                      sports,
                                      leagueMatchDays,
                                      currentMatchDay,
                                      l,
                                      hasTotwRestriction,
                                  }: Params) {
    const dispatch = useDispatch<AppDispatch>();
    const [matchday, setMatchday] = useState<number>(1);
    const leagueNumericId = leagueId ?? -1;
    const availableMatchDays = useSelector(makeSelectLeagueMatchDaysById(leagueNumericId));

    const matchdayItems = useMemo(() => {
        const maxMatchdays = leagueMatchDays || availableMatchDays.length || 0;

        if (maxMatchdays > 0) {
            return Array.from({ length: maxMatchdays }, (_, index) => ({
                key: index + 1,
                label: `${index + 1}. ${translate("matchDay", l)}`,
            }));
        }
        return [];
    }, [availableMatchDays.length, leagueMatchDays, l]);

    const totwMatchdayItems = useMemo(() => {
        if (!hasTotwRestriction) return matchdayItems;
        if (!currentMatchDay) return matchdayItems.length ? [matchdayItems[0]] : [];
        return matchdayItems.filter((item) => item.key === currentMatchDay);
    }, [hasTotwRestriction, matchdayItems, currentMatchDay]);

    useEffect(() => {
        if (!leagueId || isCup) return;
        fetchMatchDaysOfLeague(dispatch, leagueId, new Date(), "today", sports);
    }, [leagueId, dispatch, sports, isCup]);

    useEffect(() => {
        if (!leagueId || isCup || !matchday) return;
        fetchMatchesByLeagueAndMatchDay(dispatch, leagueId, matchday);
    }, [dispatch, leagueId, matchday, isCup]);

    useEffect(() => {
        if (currentMatchDay) setMatchday(currentMatchDay);
    }, [currentMatchDay]);

    const currentMatchdayLabel = useMemo(() => {
        const currentItem = matchdayItems.find((item) => item.key === matchday);
        return currentItem ? currentItem.label : "";
    }, [matchdayItems, matchday]);

    return {
        matchday,
        setMatchday,
        matchdayItems,
        totwMatchdayItems,
        currentMatchdayLabel,
    };
}



